import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import './ResultsChart.css';

const ResultsChart = ({ percentage, color, level }) => {
  const [chartValue, setChartValue] = useState(0);
  // Redondear el porcentaje a un número entero y limitar a un máximo de 100%
  const roundedPercentage = Math.min(Math.round(percentage), 100);
  
  useEffect(() => {
    // Animación para que el gráfico se llene gradualmente
    const timer = setTimeout(() => {
      setChartValue(roundedPercentage);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [percentage, roundedPercentage]);
  
  // Determinar el nivel para etiquetas y estilos
  let levelLabel, levelRange, levelColor;
  
  if (percentage < 20) {
    levelLabel = 'Bajo';
    levelRange = '0-20%';
    levelColor = '#4ecdc4'; // Verde-azulado para nivel bajo
  } else if (percentage < 40) {
    levelLabel = 'Leve';
    levelRange = '20-40%';
    levelColor = '#97d5a0'; // Verde claro para nivel leve
  } else if (percentage < 60) {
    levelLabel = 'Moderado';
    levelRange = '40-60%';
    levelColor = '#ff9800'; // Naranja para nivel moderado
  } else if (percentage < 80) {
    levelLabel = 'Significativo';
    levelRange = '60-80%';
    levelColor = '#ff7043'; // Naranja-rojo para nivel significativo
  } else {
    levelLabel = 'Intenso';
    levelRange = '80-100%';
    levelColor = '#ff5252'; // Rojo para nivel intenso
  }
  
  // Calcular la posición de la aguja en el medidor (0 a 180 grados)
  // const needleRotation = (chartValue / 100) * 180;
  
  return (
    <div className="results-chart">
      <div className="chart-title">Nivel de Malestar Emocional</div>
      
      <motion.div 
        className="gauge-container"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="gauge-bg">
          <div className="gauge-levels">
            <div className="level-zone level-very-low">
              <span>Bajo</span>
            </div>
            <div className="level-zone level-low">
              <span>Leve</span>
            </div>
            <div className="level-zone level-medium">
              <span>Moderado</span>
            </div>
            <div className="level-zone level-high">
              <span>Significativo</span>
            </div>
            <div className="level-zone level-very-high">
              <span>Intenso</span>
            </div>
          </div>
          
          {/* Barras de nivel independientes */}
          <div className="level-bars-container">
            <motion.div 
              className={`level-bar very-low ${percentage < 20 ? 'active' : ''}`}
              initial={{ height: 0 }}
              animate={{ height: percentage < 20 ? '100%' : '20%' }}
              transition={{ duration: 1, delay: 0.3 }}
            />
            <motion.div 
              className={`level-bar low ${percentage >= 20 && percentage < 40 ? 'active' : ''}`}
              initial={{ height: 0 }}
              animate={{ height: (percentage >= 20 && percentage < 40) ? '100%' : '20%' }}
              transition={{ duration: 1, delay: 0.4 }}
            />
            <motion.div 
              className={`level-bar medium ${percentage >= 40 && percentage < 60 ? 'active' : ''}`}
              initial={{ height: 0 }}
              animate={{ height: (percentage >= 40 && percentage < 60) ? '100%' : '20%' }}
              transition={{ duration: 1, delay: 0.5 }}
            />
            <motion.div 
              className={`level-bar high ${percentage >= 60 && percentage < 80 ? 'active' : ''}`}
              initial={{ height: 0 }}
              animate={{ height: (percentage >= 60 && percentage < 80) ? '100%' : '20%' }}
              transition={{ duration: 1, delay: 0.6 }}
            />
            <motion.div 
              className={`level-bar very-high ${percentage >= 80 ? 'active' : ''}`}
              initial={{ height: 0 }}
              animate={{ height: percentage >= 80 ? '100%' : '20%' }}
              transition={{ duration: 1, delay: 0.7 }}
            />
          </div>
          
          <div className="gauge-center" />
          
          {/* Eliminamos la aguja del termómetro 
          <motion.div 
            className="gauge-pointer"
            initial={{ rotate: 0 }}
            animate={{ rotate: needleRotation }}
            style={{ height: "120px" }}
            transition={{ 
              duration: 1.5, 
              delay: 0.5, 
              type: 'spring', 
              stiffness: 50 
            }}
          >
            <div className="pointer-stem" style={{ backgroundColor: levelColor }} />
            <div className="pointer-knob" style={{ backgroundColor: levelColor, boxShadow: `0 0 10px ${levelColor}` }} />
          </motion.div>
          */}
        </div>
        
        <motion.div 
          className="gauge-value"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 2 }}
        >
          <span className="value-number" style={{ color: levelColor, marginTop: '15px' }}>
            {Math.min(Math.round(chartValue), 100)}%
          </span>
        </motion.div>
      </motion.div>
      
      <motion.div 
        className="level-details"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 2.3 }}
      >
        <div className="level-badge" style={{ backgroundColor: levelColor }}>
          Nivel {levelLabel}
        </div>
        <div className="level-range">
          ({levelRange})
        </div>
      </motion.div>
    </div>
  );
};

export default ResultsChart;
